import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Button,
  Select,
  useToast,
  GridItem,
  Grid,
  Heading,
  Divider,
  HStack,
  Icon,
  Text,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { MdPerson, MdEmail, MdWork, MdLock } from 'react-icons/md';

interface UsuarioDetailProps {
  usuarioId: string | null;
  onClose: () => void;
}

const UsuarioDetail: React.FC<UsuarioDetailProps> = ({ usuarioId, onClose }) => {
  const [usuario, setUsuario] = useState<any | null>(null);
  const [puestos, setPuestos] = useState<{ id: string; descripcion: string }[]>([]);
  const toast = useToast();

  useEffect(() => {
    const fetchPuestos = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/cPuestoRoutes`);
        const data = await response.json();
        const habilitados = data.filter((puesto: any) => puesto.habilitado);
        setPuestos(habilitados.map((puesto: any) => ({ id: puesto.id, descripcion: puesto.descripcion })));
      } catch (error) {
        toast({
          title: 'Error',
          description: 'No se pudieron cargar los puestos.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    };

    fetchPuestos();

    if (usuarioId) {
      const fetchUsuario = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/usuarios/${usuarioId}`);
          const data = await response.json();
          setUsuario(data[0]);
        } catch (error) {
          toast({
            title: 'Error',
            description: 'No se pudieron cargar los datos del usuario.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }
      };
      fetchUsuario();
    } else {
      setUsuario({
        idPuesto: '',
        nombre: '',
        primerApellido: '',
        segundoApellido: '',
        usuario: '',
        contrase_a: '',
        email: '',
        noControl: '',
        activo: true,
        usuarioSDE: '',
        contrase_aSDE: '',
        idMunicipio: '',
      });
    }
  }, [usuarioId]);

  const handleSave = async () => {
    try {
      const url = usuarioId
        ? `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/usuarios/${usuarioId}`
        : `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/usuarios`;

      const method = usuarioId ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(usuario),
      });

      if (response.ok) {
        toast({
          title: usuarioId ? 'Usuario Actualizado' : 'Usuario Creado',
          description: `El usuario ha sido ${
            usuarioId ? 'actualizado' : 'creado'
          } con éxito.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onClose();
      } else {
        throw new Error('Error en la operación');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo guardar el usuario.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (!usuario) return null;

  return (
    <Box p={6} bg="gray.50" borderRadius="xl">
      {/* Header */}
      <VStack spacing={4} align="stretch" mb={6}>
        <HStack spacing={3}>
          <Box
            p={3}
            bg="linear-gradient(135deg, #681a2d 0%, #5b182b 100%)"
            borderRadius="lg"
          >
            <Icon as={MdPerson} color="white" boxSize={6} />
          </Box>
          <Box>
            <Heading size="md" color="#681a2d">
              {usuarioId ? 'Editar Usuario' : 'Nuevo Usuario'}
            </Heading>
            <Text fontSize="sm" color="gray.600">
              {usuarioId ? 'Actualiza la información del usuario' : 'Completa los datos del nuevo usuario'}
            </Text>
          </Box>
        </HStack>
        <Divider borderColor="gray.300" />
      </VStack>

      {/* Formulario */}
      <Grid
        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
        gap={6}
      >
        <GridItem>
          <FormControl>
            <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">
              <HStack spacing={2}>
                <Icon as={MdWork} color="#bb8c52" />
                <Text>Puesto</Text>
              </HStack>
            </FormLabel>
            <Select
              placeholder="Seleccionar Puesto"
              value={usuario.idPuesto}
              onChange={(e) => setUsuario({ ...usuario, idPuesto: e.target.value })}
              bg="white"
              borderColor="gray.300"
              _hover={{ borderColor: "#bb8c52" }}
              _focus={{ borderColor: "#681a2d", boxShadow: "0 0 0 1px #681a2d" }}
            >
              {puestos.map((puesto) => (
                <option key={puesto.id} value={puesto.id}>
                  {puesto.descripcion}
                </option>
              ))}
            </Select>
          </FormControl>
        </GridItem>

        <GridItem>
          <FormControl>
            <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">
              <HStack spacing={2}>
                <Icon as={MdPerson} color="#bb8c52" />
                <Text>Nombre</Text>
              </HStack>
            </FormLabel>
            <Input
              value={usuario.nombre}
              onChange={(e) => setUsuario({ ...usuario, nombre: e.target.value })}
              bg="white"
              borderColor="gray.300"
              _hover={{ borderColor: "#bb8c52" }}
              _focus={{ borderColor: "#681a2d", boxShadow: "0 0 0 1px #681a2d" }}
            />
          </FormControl>
        </GridItem>

        <GridItem>
          <FormControl>
            <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">Primer Apellido</FormLabel>
            <Input
              value={usuario.primerApellido}
              onChange={(e) => setUsuario({ ...usuario, primerApellido: e.target.value })}
              bg="white"
              borderColor="gray.300"
              _hover={{ borderColor: "#bb8c52" }}
              _focus={{ borderColor: "#681a2d", boxShadow: "0 0 0 1px #681a2d" }}
            />
          </FormControl>
        </GridItem>

        <GridItem>
          <FormControl>
            <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">Segundo Apellido</FormLabel>
            <Input
              value={usuario.segundoApellido}
              onChange={(e) => setUsuario({ ...usuario, segundoApellido: e.target.value })}
              bg="white"
              borderColor="gray.300"
              _hover={{ borderColor: "#bb8c52" }}
              _focus={{ borderColor: "#681a2d", boxShadow: "0 0 0 1px #681a2d" }}
            />
          </FormControl>
        </GridItem>

        <GridItem>
          <FormControl>
            <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">Usuario</FormLabel>
            <Input
              value={usuario.usuario}
              onChange={(e) => setUsuario({ ...usuario, usuario: e.target.value })}
              bg="white"
              borderColor="gray.300"
              _hover={{ borderColor: "#bb8c52" }}
              _focus={{ borderColor: "#681a2d", boxShadow: "0 0 0 1px #681a2d" }}
            />
          </FormControl>
        </GridItem>

        <GridItem>
          <FormControl>
            <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">
              <HStack spacing={2}>
                <Icon as={MdLock} color="#bb8c52" />
                <Text>Contraseña</Text>
              </HStack>
            </FormLabel>
            <Input
              type="password"
              value={usuario.contrase_a}
              onChange={(e) => setUsuario({ ...usuario, contrase_a: e.target.value })}
              bg="white"
              borderColor="gray.300"
              _hover={{ borderColor: "#bb8c52" }}
              _focus={{ borderColor: "#681a2d", boxShadow: "0 0 0 1px #681a2d" }}
            />
          </FormControl>
        </GridItem>

        <GridItem>
          <FormControl>
            <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">
              <HStack spacing={2}>
                <Icon as={MdEmail} color="#bb8c52" />
                <Text>Email</Text>
              </HStack>
            </FormLabel>
            <Input
              type="email"
              value={usuario.email}
              onChange={(e) => setUsuario({ ...usuario, email: e.target.value })}
              bg="white"
              borderColor="gray.300"
              _hover={{ borderColor: "#bb8c52" }}
              _focus={{ borderColor: "#681a2d", boxShadow: "0 0 0 1px #681a2d" }}
            />
          </FormControl>
        </GridItem>

        <GridItem>
          <FormControl>
            <FormLabel color="gray.700" fontWeight="semibold" fontSize="sm">Estado</FormLabel>
            <HStack
              spacing={3}
              p={3}
              bg="white"
              borderRadius="md"
              borderWidth="1px"
              borderColor="gray.300"
            >
              <Switch
                size="lg"
                colorScheme="green"
                isChecked={usuario.activo}
                onChange={(e) => setUsuario({ ...usuario, activo: e.target.checked })}
              />
              <Text fontWeight="medium" color={usuario.activo ? "green.600" : "gray.500"}>
                {usuario.activo ? 'Activo' : 'Inactivo'}
              </Text>
            </HStack>
          </FormControl>
        </GridItem>
      </Grid>

      {/* Footer con botones */}
      <Divider my={6} borderColor="gray.300" />
      <HStack spacing={4} justify="flex-end">
        <Button
          variant="outline"
          borderColor="gray.400"
          color="gray.700"
          _hover={{ bg: "gray.100" }}
          onClick={onClose}
        >
          Cancelar
        </Button>
        <Button
          bg="linear-gradient(135deg, #681a2d 0%, #5b182b 100%)"
          color="white"
          _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
          _active={{ transform: "translateY(0)" }}
          transition="all 0.2s"
          onClick={handleSave}
          px={8}
        >
          {usuarioId ? 'Actualizar' : 'Crear'} Usuario
        </Button>
      </HStack>
    </Box>
  );
};

export default UsuarioDetail;
